import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowLeft, RefreshCw, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

type Row = { keys?: string[]; clicks: number; impressions: number; ctr: number; position: number };

interface Report {
  status: "ok";
  siteUrl: string;
  properties: string[];
  totals: Row | null;
  byDate: Row[];
  byPage: Row[];
  byQuery: Row[];
  byDevice: Row[];
}

const iso = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return iso(d);
};

const num = (n: number) => new Intl.NumberFormat("de-AT").format(Math.round(n));
const pct = (n: number) => `${(n * 100).toFixed(1)} %`;
const pos = (n: number) => n.toFixed(1);

const AdminSeo = () => {
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(daysAgo(2));
  const [siteUrl, setSiteUrl] = useState<string | undefined>();
  const [candidates, setCandidates] = useState<string[] | null>(null);
  const [data, setData] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (property?: string) => {
      setLoading(true);
      setError(null);
      try {
        const { data: res, error: err } = await supabase.functions.invoke("gsc-report", {
          body: { startDate, endDate, siteUrl: property ?? siteUrl },
        });
        if (err) {
          const details =
            err instanceof FunctionsHttpError ? await err.context.text() : err.message;
          let parsed: Record<string, unknown> = {};
          try {
            parsed = JSON.parse(details);
          } catch {
            /* plain text */
          }
          if (parsed.status === "selection_required") {
            setCandidates(parsed.candidates as string[]);
            setData(null);
            return;
          }
          setError(
            (parsed.message as string) ??
              (parsed.error as string) ??
              details ??
              "Unbekannter Fehler",
          );
          setData(null);
          return;
        }
        setCandidates(null);
        setData(res as Report);
        setSiteUrl((res as Report).siteUrl);
      } finally {
        setLoading(false);
      }
    },
    [startDate, endDate, siteUrl],
  );

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chartData = useMemo(
    () =>
      (data?.byDate ?? []).map((r) => ({
        date: r.keys?.[0] ?? "",
        Klicks: r.clicks,
        Impressionen: r.impressions,
        Position: Number(r.position.toFixed(1)),
      })),
    [data],
  );

  const totals = data?.totals;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto flex items-center justify-between gap-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/admin/blog">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              SEO-Tracking
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-auto"
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-auto"
            />
            <Button onClick={() => load()} disabled={loading} size="sm">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Aktualisieren
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto py-8 space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Daten konnten nicht geladen werden</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {candidates && (
          <Card>
            <CardHeader>
              <CardTitle>Property auswählen</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {candidates.map((c) => (
                <Button key={c} variant="outline" onClick={() => load(c)}>
                  {c}
                </Button>
              ))}
            </CardContent>
          </Card>
        )}

        {data && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { label: "Klicks", value: totals ? num(totals.clicks) : "–" },
                { label: "Impressionen", value: totals ? num(totals.impressions) : "–" },
                { label: "CTR", value: totals ? pct(totals.ctr) : "–" },
                { label: "Ø Position", value: totals ? pos(totals.position) : "–" },
              ].map((k) => (
                <Card key={k.label}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {k.label}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-semibold">{k.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Verlauf</CardTitle>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="Impressionen"
                      stroke="hsl(var(--muted-foreground))"
                      dot={false}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="Klicks"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Tabs defaultValue="pages">
              <TabsList>
                <TabsTrigger value="pages">Seiten</TabsTrigger>
                <TabsTrigger value="queries">Keywords</TabsTrigger>
                <TabsTrigger value="devices">Geräte</TabsTrigger>
              </TabsList>

              {(
                [
                  ["pages", "Seite", data.byPage],
                  ["queries", "Suchanfrage", data.byQuery],
                  ["devices", "Gerät", data.byDevice],
                ] as const
              ).map(([value, label, rows]) => (
                <TabsContent key={value} value={value}>
                  <Card>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{label}</TableHead>
                            <TableHead className="text-right">Klicks</TableHead>
                            <TableHead className="text-right">Impressionen</TableHead>
                            <TableHead className="text-right">CTR</TableHead>
                            <TableHead className="text-right">Ø Position</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-muted-foreground">
                                Keine Daten im gewählten Zeitraum.
                              </TableCell>
                            </TableRow>
                          )}
                          {rows.map((r) => (
                            <TableRow key={r.keys?.[0]}>
                              <TableCell className="max-w-[420px] truncate">
                                {r.keys?.[0]}
                              </TableCell>
                              <TableCell className="text-right">{num(r.clicks)}</TableCell>
                              <TableCell className="text-right">{num(r.impressions)}</TableCell>
                              <TableCell className="text-right">{pct(r.ctr)}</TableCell>
                              <TableCell className="text-right">{pos(r.position)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>

            <p className="text-xs text-muted-foreground">
              Quelle: Google Search Console – Property {data.siteUrl}. Daten sind ca. 2 Tage
              verzögert.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminSeo;
